// Unicode emoji as images. The browser normally draws emoji from a system
// font; without one they are empty boxes. These helpers find emoji in
// text and name the Twemoji picture for each, served from our own static
// directory (copied out of @twemoji/svg at build time).

export const UNICODE_EMOJI_BASE_URL = '/static/twemoji'

const RI = '[\\u{1F1E6}-\\u{1F1FF}]'
const MODIFIER = '[\\u{1F3FB}-\\u{1F3FF}]'
const TAGS = '(?:[\\u{E0020}-\\u{E007E}]+\\u{E007F})'
// A pictograph shown as emoji by default, or a text-default one (♥, ©,
// ↔) made emoji by the variation selector, skin tone or joiner after it.
// Bare © or ↔ in prose stay text.
const FIRST = `(?:\\p{Emoji_Presentation}|\\p{Extended_Pictographic}(?=\\uFE0F|${MODIFIER}|\\u200D))`
const ELEMENT = `(?:${FIRST}\\uFE0F?${MODIFIER}?${TAGS}?)`
// after a joiner anything pictographic belongs to the sequence (👁‍🗨,
// 🙋‍♂️ end in text-default characters)
const JOINED = `(?:\\u200D\\p{Extended_Pictographic}\\uFE0F?${MODIFIER}?)`
const KEYCAP = '(?:[#*0-9]\\uFE0F?\\u20E3)'
const SOURCE = `${RI}{2}|${KEYCAP}|${ELEMENT}${JOINED}*`

const PATTERN = new RegExp(SOURCE, 'gu')
const WHOLE = new RegExp(`^(?:${SOURCE})$`, 'u')

// True when the whole string is one emoji.
export const isUnicodeEmoji = (text) => WHOLE.test(text)

// A text-default pictograph without its variation selector; the
// fully qualified form of a sequence has one after each.
const UNQUALIFIED =
  /(?!\p{Emoji_Presentation})\p{Extended_Pictographic}(?!\uFE0F)/gu
const qualify = (emoji) => emoji.replace(UNQUALIFIED, '$&\uFE0F')

const hex = (text) =>
  [...text].map((c) => c.codePointAt(0).toString(16)).join('-')

// Twemoji names files by code points, leaving out U+FE0F unless the
// sequence has a joiner in it.
export const unicodeEmojiFile = (emoji) =>
  hex(emoji.includes('\u200D') ? emoji : emoji.replaceAll('\uFE0F', ''))

// Candidate picture URLs, most likely first. Twemoji names joined
// sequences in their fully qualified form (❤‍🔥 typed without the
// selector lives under 2764-fe0f-200d-1f525), except for a few that are
// under the stripped name (👁‍🗨).
export const unicodeEmojiUrls = (emoji) => {
  const joined = emoji.includes('\u200D')
  const names = [
    unicodeEmojiFile(emoji),
    joined ? hex(qualify(emoji)) : null,
    hex(emoji.replaceAll('\uFE0F', '')),
  ]
  return [...new Set(names.filter(Boolean))].map(
    (file) => `${UNICODE_EMOJI_BASE_URL}/${file}.svg`,
  )
}

// Text split at its emoji: the text parts as strings, each emoji as
// whatever the processor makes of it. Mirrors processTextForEmoji.
export const processTextForUnicodeEmoji = (text, processor) => {
  const out = []
  let last = 0
  for (const match of text.matchAll(PATTERN)) {
    if (match.index > last) out.push(text.slice(last, match.index))
    out.push(processor(match[0]))
    last = match.index + match[0].length
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}
